import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Textarea } from '@/components/ui/Textarea'
import { reasonSchema, type ReasonValues } from '../schemas'
import { FormDialog } from './FormDialog'

interface ReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  submitLabel: string
  pending: boolean
  onSubmit: (reason: string) => Promise<void>
}

/** Confirmation that requires a reason (cancel invoice, reverse payment). */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pending,
  onSubmit,
}: ReasonDialogProps) {
  const { t } = useTranslation('finance')
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ReasonValues>({ resolver: zodResolver(reasonSchema), defaultValues: { reason: '' } })

  const submit = handleSubmit(async ({ reason }) => {
    try {
      await onSubmit(reason)
    } catch (e) {
      applyServerErrors(e, setError, ['reason'])
    }
  })

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<AlertTriangle className="text-danger-600" />}
      title={title}
      description={description}
      formId="finance-reason-form"
      submitLabel={submitLabel}
      pending={pending}
      tone="danger"
      size="sm"
    >
      <form id="finance-reason-form" onSubmit={(e) => void submit(e)} noValidate>
        <FormField
          label={t('Reason')}
          error={errors.reason?.message}
          required
          hint={t('Kept on the record for the audit trail.')}
        >
          <Textarea rows={3} autoFocus {...register('reason')} />
        </FormField>
      </form>
    </FormDialog>
  )
}
